import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Megaphone, FileText, Users, Sparkles, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { runToolJob, TOOL_ETA } from "@/lib/toolJobs";
import JobProgress from "@/components/app/JobProgress";
import BrandDnaCard from "@/components/app/BrandDnaCard";
import { useBrandDnaSummary } from "@/lib/useBrandDna";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "ads", label: "Ads-Spy", icon: Megaphone, desc: "Konkurrenz-Werbeanzeigen analysieren" },
  { id: "page", label: "Page Audit", icon: FileText, desc: "Facebook Page kompletter Check" },
  { id: "group", label: "Group Listener", icon: Users, desc: "Content-Ideen aus Gruppen-Threads" },
];

function ChecklistItem({ c, i }) {
  const map = { hoch: "bg-red-500/15 text-red-400 border-red-500/30", mittel: "bg-amber-500/15 text-amber-400 border-amber-500/30", niedrig: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" };
  return (
    <li key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
      <div className="flex items-center gap-2 mb-1"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md border ${map[c.priority] || map.mittel}`}>{c.priority}</span></div>
      <p className="text-xs text-zinc-200">{c.task}</p>
      {c.impact && <p className="text-[10px] text-zinc-500 mt-0.5">→ {c.impact}</p>}
    </li>
  );
}

export default function FacebookHubPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "ads");
  const [adsQuery, setAdsQuery] = useState("");
  const [adsCountry, setAdsCountry] = useState("DE");
  const [pageUrl, setPageUrl] = useState("");
  const [groupUrl, setGroupUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(null);
  const [result, setResult] = useState(null);
  const { summary: dna } = useBrandDnaSummary();

  useEffect(() => { setSearchParams({ tab }); /* eslint-disable-next-line */ }, [tab]);

  // Pre-fill Ads-Spy query from Brand DNA niche if empty
  useEffect(() => {
    if (dna?.niche && !adsQuery) {
      // Extract first 1-3 meaningful words for the query
      const tok = (dna.niche || "").split(/[\s,.]+/).filter(w => w.length >= 4).slice(0, 2).join(" ");
      if (tok) setAdsQuery(tok);
    }
    // eslint-disable-next-line
  }, [dna]);

  // Re-open past run via ?jobId=
  useEffect(() => {
    const jobId = searchParams.get("jobId");
    if (!jobId) return;
    (async () => {
      try {
        const { data: job } = await axios.get(`${API}/tools/jobs/${jobId}`);
        if (job.status === "completed" && job.result) {
          setResult(job.result);
          if (job.tool === "facebook-ads-spy") {
            setTab("ads");
            if (job.input?.query) setAdsQuery(job.input.query);
            if (job.input?.country) setAdsCountry(job.input.country);
          } else if (job.tool === "facebook-page-audit") {
            setTab("page");
            if (job.input?.page_url) setPageUrl(job.input.page_url);
          } else if (job.tool === "facebook-group-listener") {
            setTab("group");
            if (job.input?.group_url) setGroupUrl(job.input.group_url);
          }
        } else if (job.status === "failed") {
          toast.error(job.error || "Diese Analyse ist fehlgeschlagen");
        } else if (job.status === "running") {
          toast.info("Analyse läuft noch — bitte gleich nochmal probieren");
        }
      } catch (_) { /* silent */ }
    })();
  }, [searchParams]);

  const runAds = async () => {
    if (!adsQuery.trim()) return;
    setLoading(true); setResult(null); setTick(null);
    try {
      const data = await runToolJob("post", "/tools/facebook-ads-spy", { query: adsQuery.trim(), country: adsCountry }, {
        etaSec: TOOL_ETA["facebook-ads-spy"], onTick: setTick,
      });
      setResult(data); toast.success(`${data.scraped?.ads?.length || 0} Ads geladen`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Ads-Spy fehlgeschlagen"); }
    finally { setLoading(false); setTick(null); }
  };
  const runPage = async () => {
    if (!pageUrl.trim()) return;
    setLoading(true); setResult(null); setTick(null);
    try {
      const data = await runToolJob("post", "/tools/facebook-page-audit", { page_url: pageUrl.trim() }, {
        etaSec: TOOL_ETA["facebook-page-audit"], onTick: setTick,
      });
      setResult(data); toast.success("Page-Audit fertig");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Page-Audit fehlgeschlagen"); }
    finally { setLoading(false); setTick(null); }
  };
  const runGroup = async () => {
    if (!groupUrl.trim()) return;
    setLoading(true); setResult(null); setTick(null);
    try {
      const data = await runToolJob("post", "/tools/facebook-group-listener", { group_url: groupUrl.trim() }, {
        etaSec: TOOL_ETA["facebook-group-listener"], onTick: setTick,
      });
      setResult(data); toast.success("Group-Analyse fertig");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Group-Analyse fehlgeschlagen"); }
    finally { setLoading(false); setTick(null); }
  };

  const ai = result?.ai_analysis || {};
  const scraped = result?.scraped || {};

  const switchTab = (id) => { setTab(id); setResult(null); };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="h-8 w-8 text-zinc-400 hover:text-white"><ArrowLeft className="w-4 h-4" /></Button>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Facebook-Tools</h1>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 border-b border-zinc-800 pb-3">
          {TABS.map(t => (
            <button key={t.id} onClick={() => switchTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-medium transition-all ${tab === t.id ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/40" : "text-zinc-400 hover:text-zinc-200 border border-transparent"}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {/* ADS-SPY */}
        {tab === "ads" && (
          <div className="space-y-4">
            {dna && <BrandDnaCard summary={dna} variant="hint" />}
            <p className="text-sm text-zinc-400">Spioniere Konkurrenz-Werbeanzeigen aus der Facebook Ad Library. Page-URL ODER Keyword eingeben.</p>
            <div className="flex gap-2">
              <Input value={adsQuery} onChange={e => setAdsQuery(e.target.value)} onKeyDown={e => e.key === "Enter" && runAds()} placeholder="https://facebook.com/coachname  ODER  Keyword" className="bg-zinc-900 border-zinc-800 text-white h-10 flex-1" data-testid="ads-input" />
              <Input value={adsCountry} onChange={e => setAdsCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="DE" className="bg-zinc-900 border-zinc-800 text-white h-10 w-20" />
              <Button onClick={runAds} disabled={loading || !adsQuery.trim()} className="bg-cyan-500 hover:bg-cyan-400 text-white h-10" data-testid="run-ads-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Spy starten</>}
              </Button>
            </div>
            {loading && tab === "ads" && <JobProgress tick={tick} label="Facebook Ads-Spy läuft…" />}

            {result && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">{scraped.ads?.length} aktive Ads gefunden</p>
                  <p className="text-sm text-zinc-200">{ai.summary || ai.verdict}</p>
                </div>
                {ai.common_hooks?.length > 0 && <Section title="Wiederkehrende Hooks" color="emerald">{ai.common_hooks.map((h, i) => <li key={i} className="text-xs text-emerald-200/90">▸ {h}</li>)}</Section>}
                {ai.common_ctas?.length > 0 && <Section title="Common CTAs" color="cyan">{ai.common_ctas.map((c, i) => <li key={i} className="text-xs text-cyan-200/90">▸ {c}</li>)}</Section>}
                {ai.what_works_likely?.length > 0 && <Section title="Was wahrscheinlich funktioniert" color="pink">{ai.what_works_likely.map((w, i) => <li key={i} className="text-xs text-cyan-200/90">→ {w}</li>)}</Section>}
                {ai.your_actions?.length > 0 && <Section title="Deine konkreten Schritte" color="pink">{ai.your_actions.map((a, i) => <li key={i} className="text-xs text-zinc-200">{i + 1}. {a}</li>)}</Section>}
                {ai.compliance_risks?.length > 0 && <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20"><p className="text-xs font-medium text-amber-400 mb-1 flex gap-1"><AlertTriangle className="w-3.5 h-3.5" />Compliance-Risiken</p><ul className="text-xs text-amber-200/80 space-y-0.5">{ai.compliance_risks.map((r, i) => <li key={i}>▸ {r}</li>)}</ul></div>}
                {scraped.ads?.length > 0 && (
                  <details className="rounded-xl bg-zinc-900 border border-zinc-800 p-3">
                    <summary className="text-xs text-zinc-400 cursor-pointer">Alle {scraped.ads.length} Ads anzeigen</summary>
                    <div className="mt-2 space-y-2">
                      {scraped.ads.map((a, i) => (
                        <div key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                          <p className="text-xs font-semibold text-white">{a.page_name}</p>
                          {a.title && <p className="text-xs text-cyan-300 mt-0.5">{a.title}</p>}
                          <p className="text-xs text-zinc-300 mt-1 line-clamp-3">{a.ad_text}</p>
                          {a.cta_text && <p className="text-[10px] text-cyan-400 mt-1">CTA: {a.cta_text}</p>}
                          {a.ad_url && <a href={a.ad_url} target="_blank" rel="noreferrer" className="text-[10px] text-zinc-500 hover:text-cyan-400">Original →</a>}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )}

        {/* PAGE AUDIT */}
        {tab === "page" && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Audit einer Facebook-Page — Content-Muster, Top-Format, Verbesserungs-Checkliste.</p>
            <div className="flex gap-2">
              <Input value={pageUrl} onChange={e => setPageUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && runPage()} placeholder="https://www.facebook.com/pagename" className="bg-zinc-900 border-zinc-800 text-white h-10 flex-1" data-testid="page-url-input" />
              <Button onClick={runPage} disabled={loading || !pageUrl.trim()} className="bg-cyan-500 hover:bg-cyan-400 text-white h-10" data-testid="run-page-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Audit</>}
              </Button>
            </div>
            {loading && tab === "page" && <JobProgress tick={tick} label="Page-Audit läuft…" />}
            {result && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">{scraped.page_name} · Score {ai.overall_score}/100</p>
                  <p className="text-sm text-zinc-200">{ai.verdict}</p>
                </div>
                {ai.content_patterns?.length > 0 && <Section title="Content-Muster" color="cyan">{ai.content_patterns.map((p, i) => <li key={i} className="text-xs text-cyan-200/90">▸ {p}</li>)}</Section>}
                {ai.top_performing_pattern && <div className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20"><p className="text-xs text-emerald-400 font-medium mb-1">Top-Format</p><p className="text-xs text-emerald-200/90">{ai.top_performing_pattern}</p></div>}
                {ai.weaknesses?.length > 0 && <Section title="Schwächen" color="red">{ai.weaknesses.map((w, i) => <li key={i} className="text-xs text-red-200/90">▸ {w}</li>)}</Section>}
                {ai.improvement_checklist?.length > 0 && (
                  <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                    <p className="text-sm font-semibold text-white mb-2 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-400" />Verbesserungs-Checkliste</p>
                    <ul className="space-y-2">{ai.improvement_checklist.map((c, i) => <ChecklistItem c={c} i={i} key={i} />)}</ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* GROUP LISTENER */}
        {tab === "group" && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Höre rein in Nischen-Gruppen — Audience-Pains, Wunsch-Themen und konkrete Content-Ideen aus echten Threads.</p>
            <div className="flex gap-2">
              <Input value={groupUrl} onChange={e => setGroupUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && runGroup()} placeholder="https://www.facebook.com/groups/groupname" className="bg-zinc-900 border-zinc-800 text-white h-10 flex-1" data-testid="group-url-input" />
              <Button onClick={runGroup} disabled={loading || !groupUrl.trim()} className="bg-cyan-500 hover:bg-cyan-400 text-white h-10" data-testid="run-group-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Listen</>}
              </Button>
            </div>
            {loading && tab === "group" && <JobProgress tick={tick} label="Group Listener läuft…" />}
            {result && (
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-zinc-900 border border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">{scraped.group_name} · {scraped.posts?.length} Posts ausgewertet</p>
                  <p className="text-sm text-zinc-200">{ai.summary}</p>
                </div>
                {ai.audience_pains?.length > 0 && <Section title="Audience-Pains" color="amber">{ai.audience_pains.map((p, i) => <li key={i} className="text-xs text-amber-200/90">▸ {p}</li>)}</Section>}
                {ai.audience_wins?.length > 0 && <Section title="Audience-Wins" color="emerald">{ai.audience_wins.map((w, i) => <li key={i} className="text-xs text-emerald-200/90">✓ {w}</li>)}</Section>}
                {ai.audience_language?.length > 0 && <div className="p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20"><p className="text-sm font-semibold text-emerald-300 mb-2">Audience-Sprache</p><div className="flex flex-wrap gap-1.5">{ai.audience_language.map((l, i) => <span key={i} className="text-xs px-2 py-1 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">"{l}"</span>)}</div></div>}
                {ai.trending_questions?.length > 0 && (
                  <div className="p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20">
                    <p className="text-sm font-semibold text-cyan-300 mb-2">Trending-Fragen</p>
                    <div className="space-y-2">{ai.trending_questions.map((q, i) => (
                      <div key={i} className="p-3 rounded-lg bg-zinc-950 border border-zinc-800">
                        <p className="text-xs font-medium text-cyan-300">{q.question} <span className="text-[10px] text-zinc-500 ml-1">({q.frequency})</span></p>
                        {q.content_idea && <p className="text-[11px] text-cyan-400 mt-1">→ {q.content_idea}</p>}
                      </div>
                    ))}</div>
                  </div>
                )}
                {ai.post_ideas?.length > 0 && <Section title="Deine 7 nächsten Posts" color="pink">{ai.post_ideas.map((p, i) => <li key={i} className="text-xs text-zinc-200 flex gap-2"><span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span><span>{p}</span></li>)}</Section>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, color = "cyan", children }) {
  const bg = { emerald: "bg-emerald-500/5 border-emerald-500/20", cyan: "bg-cyan-500/5 border-cyan-500/20", pink: "bg-cyan-500/5 border-cyan-500/20", amber: "bg-amber-500/5 border-amber-500/20", red: "bg-red-500/5 border-red-500/20" }[color];
  const txt = { emerald: "text-emerald-300", cyan: "text-cyan-300", pink: "text-cyan-300", amber: "text-amber-300", red: "text-red-300" }[color];
  return (
    <div className={`p-4 rounded-xl border ${bg}`}>
      <p className={`text-sm font-semibold mb-2 ${txt}`}>{title}</p>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}
